/**
 * NavigatorX Cancellation Analysis Dashboard
 *
 * Subscription churn analytics and cancellation insights:
 * - Churn rate metrics and trends
 * - Cancellation reason breakdown
 * - Cancellation offer effectiveness (free month retention)
 * - Time-to-churn analysis
 * - User cancellation feedback review
 * - Admin vs user-initiated cancellation tracking
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  UserMinus,
  TrendingDown,
  TrendingUp,
  Clock,
  Gift,
  RefreshCw,
  Loader2,
  Users,
  AlertTriangle,
  MessageSquare,
  BarChart3,
  Shield,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  collectionGroup
} from 'firebase/firestore';
import { navDb } from '../../../utils/firebase/client';
import { NAV_USERS_COLLECTION, timestampToDate } from '../../../utils/services/navigatorXService';
import { getBillingStats, type BillingStats } from '../../../utils/services/navigatorXBillingService';
import type { NavUser } from '../../../types/navigatorx';

interface CancellationAnalysisProps {
  embedded?: boolean;
}

interface CancellationRecord {
  userId: string;
  displayName: string;
  email: string;
  cancelledAt: Date | null;
  cancelReason: string;
  cancelledBy: string | null;
  cancellationFreeMonthUsed: boolean;
  subscriptionStartDate: Date | null;
  trialStartDate: Date | null;
  daysBeforeChurn: number | null;
}

interface FeedbackEntry {
  userId: string;
  reason: string;
  feedback: string;
  createdAt: Date | null;
}

export default function CancellationAnalysis({ embedded = false }: CancellationAnalysisProps) {
  const [loading, setLoading] = useState(true);
  const [billingStats, setBillingStats] = useState<BillingStats | null>(null);
  const [cancellations, setCancellations] = useState<CancellationRecord[]>([]);
  const [feedbackEntries, setFeedbackEntries] = useState<FeedbackEntry[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [billing, usersSnapshot] = await Promise.all([
        getBillingStats(),
        getDocs(collection(navDb, NAV_USERS_COLLECTION))
      ]);

      setBillingStats(billing);

      const users: NavUser[] = [];
      const cancelledRecords: CancellationRecord[] = [];
      let userCount = 0;

      usersSnapshot.docs.forEach(doc => {
        if (['blocked', 'invites', 'admin_logs'].includes(doc.id)) return;
        userCount++;
        const data = { id: doc.id, ...doc.data() } as NavUser;
        users.push(data);

        if (data.cancelledAt || data.subscriptionStatus === 'cancelled' || data.subscriptionStatus === 'SUBSCRIPTION_EXPIRED') {
          const cancelDate = timestampToDate(data.cancelledAt);
          const subStart = timestampToDate(data.subscriptionStartDate);
          const trialStart = timestampToDate(data.trialStartDate);
          const startDate = subStart || trialStart;

          let daysBeforeChurn: number | null = null;
          if (cancelDate && startDate) {
            daysBeforeChurn = Math.floor((cancelDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          }

          cancelledRecords.push({
            userId: data.id,
            displayName: data.displayName || data.email || 'Unknown',
            email: data.email || '',
            cancelledAt: cancelDate,
            cancelReason: data.cancelReason || 'Not specified',
            cancelledBy: data.cancelledBy || null,
            cancellationFreeMonthUsed: data.cancellationFreeMonthUsed || false,
            subscriptionStartDate: subStart,
            trialStartDate: trialStart,
            daysBeforeChurn
          });
        }
      });

      cancelledRecords.sort((a, b) => {
        if (!a.cancelledAt) return 1;
        if (!b.cancelledAt) return -1;
        return b.cancelledAt.getTime() - a.cancelledAt.getTime();
      });

      setTotalUsers(userCount);
      setCancellations(cancelledRecords);

      // Load cancellation feedback from subcollections
      const allFeedback: FeedbackEntry[] = [];
      for (const record of cancelledRecords.slice(0, 50)) {
        try {
          const feedbackSnap = await getDocs(
            query(
              collection(navDb, NAV_USERS_COLLECTION, record.userId, 'cancellation_feedback'),
              orderBy('createdAt', 'desc'),
              limit(5)
            )
          );
          feedbackSnap.docs.forEach(fdoc => {
            const fdata = fdoc.data();
            allFeedback.push({
              userId: record.userId,
              reason: fdata.reason || record.cancelReason,
              feedback: fdata.feedback || '',
              createdAt: timestampToDate(fdata.createdAt)
            });
          });
        } catch {
          // User may not have feedback subcollection
        }
      }

      allFeedback.sort((a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.getTime() - a.createdAt.getTime();
      });

      setFeedbackEntries(allFeedback);
    } catch (error) {
      console.error('Error loading cancellation data:', error);
      toast.error('Failed to load cancellation analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      </div>
    );
  }

  const churnRate = billingStats?.churnRate || 0;
  const adminCancellations = cancellations.filter(c => c.cancelledBy);
  const userCancellations = cancellations.filter(c => !c.cancelledBy);
  const freeMonthAccepted = cancellations.filter(c => c.cancellationFreeMonthUsed);
  const freeMonthOffered = cancellations.length;
  const retentionRate = freeMonthOffered > 0
    ? ((freeMonthAccepted.length / freeMonthOffered) * 100)
    : 0;

  // Cancellation reason breakdown
  const reasonCounts: Record<string, number> = {};
  cancellations.forEach(c => {
    const reason = c.cancelReason || 'Not specified';
    reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
  });
  const sortedReasons = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1]);

  // Time-to-churn distribution
  const churnBuckets = { '0-7 days': 0, '8-30 days': 0, '31-90 days': 0, '90+ days': 0 };
  cancellations.forEach(c => {
    if (c.daysBeforeChurn === null) return;
    if (c.daysBeforeChurn <= 7) churnBuckets['0-7 days']++;
    else if (c.daysBeforeChurn <= 30) churnBuckets['8-30 days']++;
    else if (c.daysBeforeChurn <= 90) churnBuckets['31-90 days']++;
    else churnBuckets['90+ days']++;
  });

  const avgDaysToChurn = cancellations
    .filter(c => c.daysBeforeChurn !== null && c.daysBeforeChurn > 0)
    .reduce((sum, c, _, arr) => sum + (c.daysBeforeChurn || 0) / arr.length, 0);

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Cancellation Analysis</h2>
            <p className="text-gray-500">Churn metrics and cancellation insights</p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600 font-medium">Monthly Churn Rate</p>
                <p className="text-2xl font-bold text-red-700">{churnRate.toFixed(1)}%</p>
              </div>
              <TrendingDown className="h-8 w-8 text-red-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-medium">Total Cancellations</p>
                <p className="text-2xl font-bold text-orange-700">{cancellations.length}</p>
                <p className="text-xs text-gray-500">of {totalUsers} users</p>
              </div>
              <UserMinus className="h-8 w-8 text-orange-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">Retention Offer Rate</p>
                <p className="text-2xl font-bold text-green-700">{retentionRate.toFixed(1)}%</p>
                <p className="text-xs text-gray-500">{freeMonthAccepted.length} accepted free month</p>
              </div>
              <Gift className="h-8 w-8 text-green-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Avg Days to Churn</p>
                <p className="text-2xl font-bold text-blue-700">{avgDaysToChurn > 0 ? Math.round(avgDaysToChurn) : 'N/A'}</p>
                <p className="text-xs text-gray-500">from subscription start</p>
              </div>
              <Clock className="h-8 w-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-5 w-5 text-gray-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{userCancellations.length}</p>
            <p className="text-xs text-gray-500">User-Initiated</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Shield className="h-5 w-5 text-gray-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{adminCancellations.length}</p>
            <p className="text-xs text-gray-500">Admin-Initiated</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <MessageSquare className="h-5 w-5 text-gray-400 mx-auto mb-1" />
            <p className="text-xl font-bold text-gray-700">{feedbackEntries.length}</p>
            <p className="text-xs text-gray-500">Feedback Received</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Time-to-Churn Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Time to Churn
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.values(churnBuckets).every(v => v === 0) ? (
              <p className="text-center text-gray-500 py-8">No churn data</p>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <RechartsBarChart
                  data={Object.entries(churnBuckets).map(([bucket, count]) => ({ bucket, count }))}
                  layout="vertical"
                  margin={{ top: 5, right: 30, bottom: 5, left: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af" allowDecimals={false} />
                  <YAxis type="category" dataKey="bucket" tick={{ fontSize: 11 }} stroke="#9ca3af" width={75} />
                  <Tooltip
                    formatter={(value: number) => [value, 'Users']}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {Object.entries(churnBuckets).map(([bucket], index) => (
                      <Cell
                        key={bucket}
                        fill={['#f87171', '#fb923c', '#fbbf24', '#a78bfa'][index] || '#f87171'}
                      />
                    ))}
                  </Bar>
                </RechartsBarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Cancellation Reasons */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Cancellation Reasons
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sortedReasons.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No cancellation reasons recorded</p>
            ) : (
              <div className="space-y-2">
                {sortedReasons.slice(0, 8).map(([reason, count]) => (
                  <div key={reason} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm text-gray-700 truncate flex-1 mr-2">{reason}</span>
                    <Badge variant="outline" className="flex-shrink-0">
                      {count}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Cancellation Feedback */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-red-500" />
            Recent Cancellation Feedback
          </CardTitle>
        </CardHeader>
        <CardContent>
          {feedbackEntries.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No cancellation feedback received yet</p>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {feedbackEntries.slice(0, 20).map((entry, index) => (
                <div key={index} className="p-3 border rounded-lg bg-red-50/50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {entry.reason && (
                        <Badge className="bg-red-100 text-red-700 text-xs mb-1">
                          {entry.reason}
                        </Badge>
                      )}
                      {entry.feedback && (
                        <p className="text-sm text-gray-700 mt-1">&ldquo;{entry.feedback}&rdquo;</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                      {entry.createdAt ? entry.createdAt.toLocaleDateString() : 'Unknown date'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Cancellations List */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-500" />
            Recent Cancellations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {cancellations.slice(0, 25).map(record => (
              <div key={record.userId} className="p-3 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{record.displayName}</p>
                    <p className="text-xs text-gray-500">{record.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">
                      {record.cancelledAt ? record.cancelledAt.toLocaleDateString() : 'Date unknown'}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      {record.cancelledBy && (
                        <Badge className="bg-purple-100 text-purple-700 text-xs">Admin</Badge>
                      )}
                      {record.cancellationFreeMonthUsed && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <Gift className="h-3 w-3 mr-0.5" />
                          Used offer
                        </Badge>
                      )}
                      {record.daysBeforeChurn !== null && (
                        <Badge variant="outline" className="text-xs">{record.daysBeforeChurn}d</Badge>
                      )}
                    </div>
                  </div>
                </div>
                {record.cancelReason !== 'Not specified' && (
                  <p className="text-xs text-gray-600 mt-1">Reason: {record.cancelReason}</p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
